import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsUpDown } from "@fortawesome/free-solid-svg-icons";
import styles from "../../styles/common/BoxIllustration.module.css";

interface BoxIllustrationProps {
  className?: string;
  showMetrics?: boolean;
  showLegend?: boolean;
  boxStyle?: React.CSSProperties;
}

const BoxIllustration: React.FC<BoxIllustrationProps> = ({
  className,
  showMetrics = true,
  showLegend = true,
  boxStyle,
}) => {
  return (
    <div className={`${styles.wrapper} ${className || ""}`}>
      {/* Ilustración 3D de la Caja en SVG + Badges HTML */}
      <div className={styles.boxContainer} style={boxStyle}>
        <svg viewBox="0 0 320 205" className={styles.svgElement} aria-hidden="true">
          <defs>
            <linearGradient id="boxFaceTop" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#e8c89a" />
              <stop offset="100%" stopColor="#d4a96a" />
            </linearGradient>
            <linearGradient id="boxFaceLeft" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#c48a4a" />
              <stop offset="100%" stopColor="#b87a38" />
            </linearGradient>
            <linearGradient id="boxFaceRight" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#d4a05a" />
              <stop offset="100%" stopColor="#c49040" />
            </linearGradient>
            <filter id="boxShadow" x="-10%" y="-10%" width="120%" height="130%">
              <feDropShadow dx="2" dy="6" stdDeviation="5" floodColor="#00000022" />
            </filter>

            {/* Marker de flechas amarillas */}
            <marker
              id="yellowArrowStart"
              viewBox="0 0 10 10"
              refX="3"
              refY="5"
              markerWidth="5"
              markerHeight="5"
              orient="auto"
            >
              <path d="M 10 0 L 0 5 L 10 10 z" fill="#EAB308" />
            </marker>
            <marker
              id="yellowArrowEnd"
              viewBox="0 0 10 10"
              refX="7"
              refY="5"
              markerWidth="5"
              markerHeight="5"
              orient="auto"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#EAB308" />
            </marker>
          </defs>

          {/* Caras de la caja 3D */}
          <polygon
            points="70,40 210,0 290,35 150,75"
            fill="url(#boxFaceTop)"
            stroke="#a06830"
            strokeWidth="1"
            filter="url(#boxShadow)"
          />
          <polygon
            points="70,40 150,75 150,170 70,135"
            fill="url(#boxFaceLeft)"
            stroke="#8a5820"
            strokeWidth="1"
          />
          <polygon
            points="150,75 290,35 290,130 150,170"
            fill="url(#boxFaceRight)"
            stroke="#9a6828"
            strokeWidth="1"
          />

          {/* Cinta de embalaje */}
          <polygon
            points="133,21 147,19 227,54 213,56"
            fill="#e6cc9f"
            opacity="0.8"
            stroke="#b5996b"
            strokeWidth="0.5"
          />
          <polygon
            points="213,56 227,54 227,70 213,72"
            fill="#d6b888"
            opacity="0.85"
            stroke="#a38657"
            strokeWidth="0.5"
          />

          {/* Bordes principales de la caja */}
          <line x1="70" y1="40" x2="70" y2="135" stroke="#7a4818" strokeWidth="1.5" />
          <line x1="290" y1="35" x2="290" y2="130" stroke="#7a4818" strokeWidth="1.5" />
          <line x1="70" y1="135" x2="150" y2="170" stroke="#7a4818" strokeWidth="1.5" />
          <line x1="150" y1="170" x2="290" y2="130" stroke="#7a4818" strokeWidth="1.5" />

          {/* Flechas indicadoras de cota (SVG) */}
          {showMetrics && (
            <>
              {/* ALTO */}
              <line x1="46" y1="40" x2="72" y2="40" stroke="#666" strokeWidth="1.2" strokeDasharray="3,3" />
              <line x1="46" y1="135" x2="72" y2="135" stroke="#666" strokeWidth="1.2" strokeDasharray="3,3" />
              <line
                x1="60"
                y1="46"
                x2="60"
                y2="129"
                stroke="#EAB308"
                strokeWidth="2.5"
                markerStart="url(#yellowArrowStart)"
                markerEnd="url(#yellowArrowEnd)"
              />

              {/* ANCHO */}
              <line x1="70" y1="135" x2="58" y2="142" stroke="#666" strokeWidth="1.2" strokeDasharray="3,3" />
              <line x1="150" y1="170" x2="138" y2="177" stroke="#666" strokeWidth="1.2" strokeDasharray="3,3" />
              <line
                x1="63"
                y1="144"
                x2="133"
                y2="175"
                stroke="#EAB308"
                strokeWidth="2.5"
                markerStart="url(#yellowArrowStart)"
                markerEnd="url(#yellowArrowEnd)"
              />

              {/* LARGO */}
              <line x1="150" y1="170" x2="156" y2="182" stroke="#666" strokeWidth="1.2" strokeDasharray="3,3" />
              <line x1="290" y1="130" x2="296" y2="142" stroke="#666" strokeWidth="1.2" strokeDasharray="3,3" />
              <line
                x1="159"
                y1="178"
                x2="288"
                y2="141"
                stroke="#EAB308"
                strokeWidth="2.5"
                markerStart="url(#yellowArrowStart)"
                markerEnd="url(#yellowArrowEnd)"
              />
            </>
          )}
        </svg>

        {/* --- ETIQUETAS DE TEXTO HTML POSICIONADAS CON CSS --- */}
        {showMetrics && (
          <>
            <div className={`${styles.badge} ${styles.badgeAlto}`}>ALTO</div>
            <div className={`${styles.badge} ${styles.badgeAncho}`}>ANCHO</div>
            <div className={`${styles.badge} ${styles.badgeLargo}`}>LARGO</div>
          </>
        )}
      </div>

      {/* --- LEYENDA EXPLICATIVA HTML CON FONT AWESOME ROTADO --- */}
      {showLegend && (
        <div className={styles.legendContainer}>
          {/* ALTO */}
          <div className={styles.legendItem}>
            <div className={styles.iconCircle}>
              <FontAwesomeIcon icon={faArrowsUpDown} style={{ fontSize: "18px", color: "#000000" }} />
            </div>
            <div className={styles.legendContent}>
              <div className={styles.legendBadge}>ALTO</div>
              <p className={styles.legendDescription}>
                Medida vertical desde la base hasta la parte superior.
              </p>
            </div>
          </div>

          <div className={styles.divider} />

          {/* ANCHO */}
          <div className={styles.legendItem}>
            <div className={styles.iconCircle}>
              <FontAwesomeIcon icon={faArrowsUpDown} style={{ fontSize: "18px", color: "#000000", transform: "rotate(90deg)" }} />
            </div>
            <div className={styles.legendContent}>
              <div className={styles.legendBadge}>ANCHO</div>
              <p className={styles.legendDescription}>
                Medida horizontal de lado a lado de la base.
              </p>
            </div>
          </div>

          <div className={styles.divider} />

          {/* LARGO */}
          <div className={styles.legendItem}>
            <div className={styles.iconCircle}>
              <FontAwesomeIcon icon={faArrowsUpDown} style={{ fontSize: "18px", color: "#000000", transform: "rotate(45deg)" }} />
            </div>
            <div className={styles.legendContent}>
              <div className={styles.legendBadge}>LARGO</div>
              <p className={styles.legendDescription}>
                Medida horizontal desde el frente hacia el fondo.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BoxIllustration;
